import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eventService } from '../api/eventService';
import { userService } from '../api/userService';
import { taskService } from '../api/taskService';
import EventForm from '../components/EventForm';
import DGFilterBar from '../components/DGFilterBar';
import { useAuth } from '../auth/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar, Plus, X, ChevronDown, ChevronRight, ShieldCheck, Circle, MessageCircle, Paperclip, Upload, Eye, Download, Trash2 } from 'lucide-react';
import ContentLoader from '../components/ContentLoader';

const Events = () => {
    const { user } = useAuth();
    const location = useLocation(); // Add hook
    const navigate = useNavigate();

    const searchParams = new URLSearchParams(location.search);
    const divisionParam = searchParams.get('division');

    // Resolve Active Division logic
    const isDG = user?.roles?.some(r => r === 'DG' || r === 'DG_OFFICE');
    const isCoordinator = user?.roles?.some(r => r.startsWith('COORDINATOR_'));
    const getActiveDivisionName = () => {
        if (divisionParam) return divisionParam;
        if (isDG) return 'All';
        if (isCoordinator) return user?.division || user?.roles?.find(r => r.startsWith('COORDINATOR_'))?.split('_')[1] || 'My Division';
        return 'My';
    };
    const activeDivisionName = getActiveDivisionName();
    const displayDivisionName = (activeDivisionName === 'CORE' || activeDivisionName === 'RIS' || activeDivisionName === 'All') ? activeDivisionName : (activeDivisionName || '').replace('_', ' ');


    // State variables
    const [allEvents, setAllEvents] = useState([]); // All events fetched from API
    const [events, setEvents] = useState([]); // Events currently displayed (after filtering)
    const [users, setUsers] = useState([]);
    const [filters, setFilters] = useState({});

    const [isCreating, setIsCreating] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showNewEventDropdown, setShowNewEventDropdown] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowNewEventDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Subevent State
    const [expandedEventId, setExpandedEventId] = useState(null);
    const [subevents, setSubevents] = useState({});
    const [creatingSubeventFor, setCreatingSubeventFor] = useState(null);
    const [newSubeventData, setNewSubeventData] = useState({
        title: '', description: '', startDateTime: '', endDateTime: ''
    });
    const [subeventStatusMenuOpen, setSubeventStatusMenuOpen] = useState(null); // Track which subevent's menu is open
    const [activeCommentModal, setActiveCommentModal] = useState(null);
    const [subeventComments, setSubeventComments] = useState({});
    const [uploadingByEvent, setUploadingByEvent] = useState({});
    const [uploadCategoryByEvent, setUploadCategoryByEvent] = useState({});
    const [linkTaskSearchQuery, setLinkTaskSearchQuery] = useState('');
    const fileInputRefs = useRef({});
    const hydratedEventIdsRef = useRef(new Set());
    const MAX_FILE_BYTES = 100 * 1024 * 1024;
    const ALLOWED_EXTENSIONS = ['pdf', 'txt', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'csv'];
    const CATEGORY_OPTIONS = [
        { value: 'CONCEPT_AGENDA', label: 'Concept/Agenda' },
        { value: 'TRANSCRIPT', label: 'Transcript' },
        { value: 'FINAL_REPORT_SUMMARY', label: 'Final Report Summary' }
    ];
    const getLatestTimestamp = (item) => {
        const candidates = [item?.CreatedAt, item?.UpdatedAt, item?.StartDateTime, item?.EndDateTime];
        for (const value of candidates) {
            const ts = value ? new Date(value).getTime() : NaN;
            if (!Number.isNaN(ts)) return ts;
        }
        return 0;
    };

    // User roles for permissions
    const isEmployee = !isDG && !isCoordinator;

    // Helpers
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const options = { day: 'numeric', month: 'short', year: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const formatEventDate = (dateString) => {
        if (!dateString) return <span style={{ color: '#999' }}>No Date</span>;
        const date = new Date(dateString);
        date.setHours(0, 0, 0, 0); // Normalize event date to midnight
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize today to midnight

        const diffTime = date - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return <span style={{ color: '#dc3545', fontWeight: 500 }}>Overdue by {Math.abs(diffDays)} days</span>;
        if (diffDays === 0) return <span style={{ color: '#e67e22', fontWeight: 600 }}>Starts Today</span>;
        if (diffDays === 1) return <span style={{ color: '#2ecc71', fontWeight: 500 }}>Starts Tomorrow</span>;
        if (diffDays <= 7) return <span style={{ color: '#2ecc71', fontWeight: 500 }}>Starts in {diffDays} days</span>;
        return <span style={{ color: '#666' }}>{formatDate(dateString)}</span>;
    };

    const getUserName = (id, usersList) => {
        if (!id) return 'Unknown';
        const u = usersList.find(user => user.UserId === id || user.id === id);
        return u ? u.Name : 'Unknown';
    };

    const queryClient = useQueryClient();

    const { data: usersData = [], isLoading: loadingUsers } = useQuery({
        queryKey: ['users'],
        queryFn: () => userService.getAllUsers().catch(() => []),
        staleTime: 300000,
        enabled: (isDG || isCoordinator)
    });

    const usersList = useMemo(() => Array.isArray(usersData) ? usersData : (usersData?.Users || []), [usersData]);

    const { data: tasksData = [] } = useQuery({
        queryKey: ['tasks', divisionParam, activeDivisionName, isDG, isCoordinator],
        queryFn: async () => {
            if (divisionParam) return await taskService.getDivisionTasks(divisionParam);
            if (isCoordinator) {
                const userDivision = user?.division || user?.roles?.find(r => r.startsWith('COORDINATOR_'))?.split('_')[1];
                if (userDivision) return await taskService.getDivisionTasks(userDivision);
                return await taskService.getAll();
            }
            if (isDG) return await taskService.getAllTasks();
            return await taskService.getAll();
        },
        staleTime: 60000,
        enabled: !!user
    });
    const tasksList = useMemo(() => Array.isArray(tasksData) ? tasksData : (tasksData?.Items || []), [tasksData]);

    // Link Tasks State
    const [editingLinkFor, setEditingLinkFor] = useState(null);
    const [tempLinkedTasks, setTempLinkedTasks] = useState([]);
    const [loadingLinks, setLoadingLinks] = useState(false);

    const { data: allEventsData = [], isLoading: loadingEvents, error: eventsErrorObj } = useQuery({
        queryKey: ['events', divisionParam, activeDivisionName, isDG, isCoordinator],
        queryFn: async () => {
            if (divisionParam) {
                return await eventService.getDivisionEvents(divisionParam);
            } else if (isCoordinator) {
                const userDivision = user?.division || user?.roles?.find(r => r.startsWith('COORDINATOR_'))?.split('_')[1];
                if (userDivision) {
                    const [divEvents, myEvents] = await Promise.all([
                        eventService.getDivisionEvents(userDivision),
                        eventService.getAll()
                    ]);
                    const merged = [...(divEvents || []), ...(myEvents || [])];
                    const uniqueMapped = new Map(merged.map(item => [item.EventID, item]));
                    return Array.from(uniqueMapped.values());
                } else {
                    return await eventService.getAll();
                }
            } else if (isDG) {
                return await eventService.getAllEvents();
            } else {
                return await eventService.getAll();
            }
        },
        staleTime: 60000,
        enabled: !!user
    });

    useEffect(() => {
        setAllEvents(allEventsData || []);
    }, [allEventsData]);

    useEffect(() => {
        setUsers(usersList);
    }, [usersList]);

    useEffect(() => {
        setLoading(loadingEvents || loadingUsers);
    }, [loadingEvents, loadingUsers]);

    useEffect(() => {
        if (eventsErrorObj) setError(eventsErrorObj.message);
    }, [eventsErrorObj]);

    // Hydrate missing subevent counters so progress is stable before expand/collapse.
    useEffect(() => {
        const candidates = allEvents.filter(e =>
            e?.EventID &&
            !hydratedEventIdsRef.current.has(e.EventID) &&
            (typeof e.TotalSubEvents !== 'number' || typeof e.CompletedSubEvents !== 'number')
        );

        if (candidates.length === 0) return;
        let cancelled = false;

        const hydrate = async () => {
            const counterUpdates = {};
            const subeventUpdates = {};

            await Promise.all(candidates.map(async (event) => {
                hydratedEventIdsRef.current.add(event.EventID);
                try {
                    const subs = await eventService.getSubEvents(event.EventID);
                    const total = subs.length;
                    const completed = subs.filter(s => s.Status === 'Completed').length;
                    counterUpdates[event.EventID] = { TotalSubEvents: total, CompletedSubEvents: completed };
                    subeventUpdates[event.EventID] = subs;
                } catch (e) {
                    console.warn(`Failed to hydrate subevents for event ${event.EventID}`, e);
                }
            }));

            if (cancelled || Object.keys(counterUpdates).length === 0) return;

            setSubevents(prev => ({ ...prev, ...subeventUpdates }));
            setAllEvents(prev => prev.map(e => counterUpdates[e.EventID] ? { ...e, ...counterUpdates[e.EventID] } : e));
        };

        hydrate();
        return () => { cancelled = true; };
    }, [allEvents]);

    // Event status transitions are backend-owned (scheduler/worker).
    // Frontend remains read-only for timeline-based status changes.

    // Filter Tabs
    const [activeFilter, setActiveFilter] = useState('All');

    useEffect(() => {
        let filtered = [...allEvents];
        const searchParams = new URLSearchParams(location.search);
        const urlFilter = searchParams.get('filter');

        // 0. URL Filtering (Dashboard Links)
        if (urlFilter) {
            if (urlFilter === 'next7') {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                filtered = filtered.filter(e => {
                    if (!e.StartDateTime) return false;
                    const d = new Date(e.StartDateTime);
                    d.setHours(0, 0, 0, 0);
                    const diffTime = d - today;
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return diffDays >= 0 && diffDays <= 7;
                });
            } else if (['Conference', 'Meeting', 'Visit', 'Workshop', 'Other'].includes(urlFilter)) {
                filtered = filtered.filter(e => e.EventType === urlFilter);
            }
        } else {
            // 0. Tab Filtering
            if (activeFilter !== 'All') {
                filtered = filtered.filter(e => e.Category === activeFilter || e.EventType === activeFilter);
            }
        }

        // 1. Global Filter Bar (Search, Date, Status, InvolvedOnly)
        if (filters && Object.keys(filters).length > 0) {
            const { searchTerm, startDate, endDate, status, involvedOnly } = filters;

            // Date Filtering
            if (startDate) {
                const sDate = new Date(startDate);
                sDate.setHours(0, 0, 0, 0);
                filtered = filtered.filter(e => new Date(e.StartDateTime || e.CreatedAt || e.UpdatedAt || Date.now()) >= sDate);
            }
            if (endDate) {
                const eDate = new Date(endDate);
                eDate.setHours(23, 59, 59, 999);
                filtered = filtered.filter(e => new Date(e.StartDateTime || e.CreatedAt || e.UpdatedAt || Date.now()) <= eDate);
            }

            // Status Filtering
            if (status && status !== '' && status !== 'All') {
                filtered = filtered.filter(e => e.Status === status);
            }

            // Involved Only Filtering
            if (involvedOnly) {
                const myId = String(user?.sub || user?.Username || user?.UserId || user?.id || '');
                const myEmail = String(user?.Email || user?.Attributes?.find(a => a.Name === 'email')?.Value || '');
                const myName = String(user?.Name || user?.name || user?.Attributes?.find(a => a.Name === 'name')?.Value || '');

                filtered = filtered.filter(e => {
                    const eCreatorId = String(e.CreatedByID || '');
                    const eCreatorName = String(e.CreatedBy || '');

                    const isCreator = (eCreatorId && eCreatorId === myId) ||
                        (eCreatorName && (eCreatorName === myName || eCreatorName === myEmail || eCreatorName === user?.Username || eCreatorName === myId));

                    const isMember = e.Members && Array.isArray(e.Members) && e.Members.some(m => {
                        const memberStr = String(m || '');
                        return memberStr === myId || memberStr === myName || memberStr === myEmail || memberStr === user?.Username;
                    });

                    return isCreator || isMember;
                });
            }

            // Search Filtering
            if (searchTerm && searchTerm.trim() !== '') {
                const term = searchTerm.toLowerCase();
                const matchingUserIds = new Set(users.filter(u =>
                    (u.Name && u.Name.toLowerCase().includes(term)) ||
                    (u.Email && u.Email.toLowerCase().includes(term)) ||
                    (u.Division && u.Division.toLowerCase().includes(term))
                ).map(u => String(u.UserId || u.Username || u.id)));

                filtered = filtered.filter(e =>
                    (e.Division && String(e.Division).toLowerCase().includes(term)) ||
                    (e.Title && String(e.Title).toLowerCase().includes(term)) ||
                    (e.CreatedBy && matchingUserIds.has(String(e.CreatedBy))) ||
                    (e.CreatedBy && String(e.CreatedBy).toLowerCase().includes(term)) ||
                    (e.Members && Array.isArray(e.Members) && e.Members.some(m => matchingUserIds.has(String(m)) || String(m).toLowerCase().includes(term)))
                );
            }
        }

        filtered.sort((a, b) => getLatestTimestamp(b) - getLatestTimestamp(a));
        setEvents(filtered);
    }, [filters, allEvents, isDG, users, activeFilter, user, location.search]);

    const [initialFormState, setInitialFormState] = useState(null);

    const ALLOWED_EVENT_TYPES = ["Academic Events", "Engagements", "Other"];

    const handleCreate = async (eventData) => {
        try {
            const { attachmentsFiles = [], attachmentsCategory = 'CONCEPT_AGENDA', ...baseEventData } = eventData || {};
            const created = await eventService.create(baseEventData);
            const createdEventId = created?.eventId;

            if (createdEventId && Array.isArray(attachmentsFiles) && attachmentsFiles.length > 0) {
                console.log(`[Events] Starting upload for ${attachmentsFiles.length} files to event ${createdEventId}`);
                await uploadAttachmentsForEvent(createdEventId, attachmentsFiles, attachmentsCategory, true);
                console.log(`[Events] Upload sequence completed`);
            }

            setIsCreating(false);
            setInitialFormState(null);
            queryClient.invalidateQueries({ queryKey: ['events'] });
        } catch (err) {
            console.error("Failed to create event", err);
            alert("Failed to create event");
        }
    };

    const upsertEventAttachment = (eventId, attachment) => {
        const merge = (collection) => collection.map(ev => {
            if (ev.EventID !== eventId) return ev;
            const current = Array.isArray(ev.Attachments) ? ev.Attachments : [];
            const next = [...current.filter(a => a.AttachmentID !== attachment.AttachmentID), attachment];
            return { ...ev, Attachments: next };
        });
        setEvents(prev => merge(prev));
        setAllEvents(prev => merge(prev));
    };

    const validateFiles = (files) => {
        const valid = [];
        const invalid = [];
        for (const file of Array.from(files || [])) {
            const ext = ((file?.name || '').split('.').pop() || '').toLowerCase();
            if (!ALLOWED_EXTENSIONS.includes(ext)) {
                invalid.push(`${file.name}: unsupported file type`);
                continue;
            }
            if ((file?.size || 0) > MAX_FILE_BYTES) {
                invalid.push(`${file.name}: exceeds 100MB`);
                continue;
            }
            valid.push(file);
        }
        return { valid, invalid };
    };

    const uploadAttachmentsForEvent = async (eventId, files, category = 'CONCEPT_AGENDA', silent = false) => {
        const { valid: list, invalid } = validateFiles(files);
        if (invalid.length > 0 && !silent) {
            alert(`Some files were rejected:\n${invalid.join('\n')}`);
        }
        if (list.length === 0) return;

        setUploadingByEvent(prev => ({ ...prev, [eventId]: { total: list.length, done: 0, active: true } }));

        for (const file of list) {
            try {
                const prep = await eventService.prepareAttachment(eventId, file, category);
                const uploadUrl = prep?.uploadUrl;
                const requiredHeaders = prep?.requiredHeaders || {};
                const attachmentMeta = prep?.attachment;

                if (!uploadUrl || !attachmentMeta) {
                    throw new Error('Upload URL generation failed');
                }

                const putResp = await fetch(uploadUrl, {
                    method: 'PUT',
                    headers: requiredHeaders,
                    body: file
                });

                if (!putResp.ok) {
                    throw new Error(`S3 upload failed (${putResp.status})`);
                }

                const committed = await eventService.commitAttachment(eventId, {
                    ...attachmentMeta,
                    fileName: file.name,
                    contentType: file.type || attachmentMeta.contentType || 'application/octet-stream',
                    sizeBytes: file.size,
                    category
                });
                if (committed?.attachment) {
                    upsertEventAttachment(eventId, committed.attachment);
                }
            } catch (err) {
                console.error('Attachment upload failed', err);
                if (!silent) {
                    alert(`Failed to upload "${file.name}": ${err.message}`);
                }
            } finally {
                setUploadingByEvent(prev => {
                    const current = prev[eventId] || { total: list.length, done: 0, active: true };
                    const done = Math.min(current.done + 1, current.total);
                    return {
                        ...prev,
                        [eventId]: {
                            total: current.total,
                            done,
                            active: done < current.total
                        }
                    };
                });
            }
        }
    };

    const handleAttachmentPicker = (eventId) => {
        const input = fileInputRefs.current[eventId];
        if (input) input.click();
    };

    const handleAttachmentSelection = (eventId, inputEvent) => {
        const files = Array.from(inputEvent?.target?.files || []);
        if (files.length === 0) return;
        const category = uploadCategoryByEvent[eventId] || 'CONCEPT_AGENDA';
        uploadAttachmentsForEvent(eventId, files, category, false);
        inputEvent.target.value = '';
    };

    const openAttachment = async (eventId, attachmentId, isDownload = false) => {
        try {
            const data = await eventService.getAttachmentUrl(eventId, attachmentId, { download: isDownload });
            if (data?.url) {
                window.open(data.url, '_blank', 'noopener,noreferrer');
            }
        } catch (err) {
            alert('Unable to open attachment: ' + err.message);
        }
    };

    const deleteAttachment = async (eventId, attachmentId) => {
        if (!window.confirm('Delete this attachment?')) return;
        try {
            await eventService.deleteAttachment(eventId, attachmentId);
            const remove = (collection) => collection.map(ev => {
                if (ev.EventID !== eventId) return ev;
                const current = Array.isArray(ev.Attachments) ? ev.Attachments : [];
                return { ...ev, Attachments: current.filter(a => a.AttachmentID !== attachmentId) };
            });
            setEvents(prev => remove(prev));
            setAllEvents(prev => remove(prev));
        } catch (err) {
            alert('Failed to delete attachment: ' + err.message);
        }
    };

    const toggleExpand = async (eventId) => {
        if (expandedEventId === eventId) {
            setExpandedEventId(null);
            return;
        }

        setExpandedEventId(eventId);
        if (!subevents[eventId]) {
            try {
                const subs = await eventService.getSubEvents(eventId);
                setSubevents(prev => ({ ...prev, [eventId]: subs }));
            } catch (err) {
                console.error("Failed to load subevents", err);
            }
        }
    };

    const handleCreateSubevent = async (eventId) => {
        try {
            await eventService.createSubEvent(eventId, newSubeventData);
            setCreatingSubeventFor(null);
            setNewSubeventData({ title: '', description: '', startDateTime: '', endDateTime: '' });

            // Refresh subevents
            const subs = await eventService.getSubEvents(eventId);
            setSubevents(prev => ({ ...prev, [eventId]: subs }));
        } catch (err) {
            alert("Failed to create subevent: " + err.message);
        }
    };

    const handleAddSubeventComment = async (eventId, subEventId, currentStatus) => {
        const text = subeventComments[subEventId] || '';
        const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
        if (wordCount === 0 || wordCount > 20) return;

        try {
            await eventService.updateSubEvent(subEventId, {
                status: currentStatus,
                comment: {
                    text: text.trim(),
                    userName: user?.Name || user?.name || user?.Username || 'Unknown'
                }
            });
            // Clear input
            setSubeventComments(prev => ({ ...prev, [subEventId]: '' }));
            // Refresh subevents
            const subs = await eventService.getSubEvents(eventId);
            setSubevents(prev => ({ ...prev, [eventId]: subs }));
        } catch (err) {
            alert("Failed to add comment: " + err.message);
        }
    };

    const handleSaveLinkedTasks = async (eventId) => {
        setLoadingLinks(true);
        try {
            await eventService.linkTasks(eventId, tempLinkedTasks);

            // Optimistic Update
            setEvents(prev => prev.map(ev => ev.EventID === eventId ? { ...ev, LinkedTaskIDs: tempLinkedTasks } : ev));
            setAllEvents(prev => prev.map(ev => ev.EventID === eventId ? { ...ev, LinkedTaskIDs: tempLinkedTasks } : ev));

            setEditingLinkFor(null);
        } catch (err) {
            console.error("Failed to link tasks", err);
            alert('Failed to link tasks: ' + err.message);
        } finally {
            setLoadingLinks(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div style={{ flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                    <Calendar />
                    {(isDG || isCoordinator)
                        ? (divisionParam ? `${displayDivisionName} Events` : `${displayDivisionName} Events`) // Show division name if DG/Coordinator
                        : 'My Events'}
                </h2>

                {/* Filter Tabs & New Event Button */}
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    {!isCreating && (
                        <div style={{ display: 'flex', background: '#f8f9fa', padding: '0.25rem', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                            {['All', ...ALLOWED_EVENT_TYPES].map(type => (
                                <button
                                    key={type}
                                    onClick={() => {
                                        setActiveFilter(type);
                                        if (searchParams.has('filter')) {
                                            searchParams.delete('filter');
                                            navigate(`${location.pathname}?${searchParams.toString()}`);
                                        }
                                    }}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        background: activeFilter === type ? 'white' : 'transparent',
                                        color: activeFilter === type ? '#007bff' : '#6c757d',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        boxShadow: activeFilter === type ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    )}

                    {!isCreating && (
                        <div ref={dropdownRef} style={{ position: 'relative' }}>
                            <button
                                onClick={() => {
                                    setShowNewEventDropdown(prev => !prev);
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                                    padding: '0.6rem 1.2rem', background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)', color: 'white',
                                    border: 'none', borderRadius: '6px', cursor: 'pointer',
                                    boxShadow: '0 4px 6px rgba(0,123,255,0.2)', fontWeight: 600,
                                    outline: 'none'
                                }}
                            >
                                <Plus size={18} /> New Event
                            </button>
                            
                            {showNewEventDropdown && (
                                <div style={{
                                    position: 'absolute',
                                    right: 0,
                                    top: 'calc(100% + 6px)',
                                    background: 'white',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '8px',
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                    padding: '0.4rem 0',
                                    zIndex: 100,
                                    width: '180px',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <button
                                        onClick={() => {
                                            setShowNewEventDropdown(false);
                                            setInitialFormState(null);
                                            setIsCreating(true);
                                        }}
                                        style={{
                                            padding: '0.6rem 1rem',
                                            background: 'none',
                                            border: 'none',
                                            textAlign: 'left',
                                            color: '#1e293b',
                                            fontSize: '0.82rem',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.4rem',
                                            transition: 'background-color 0.15s ease'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <span>📝</span> Detailed Entry
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowNewEventDropdown(false);
                                            navigate('/event-planner');
                                        }}
                                        style={{
                                            padding: '0.6rem 1rem',
                                            background: 'none',
                                            border: 'none',
                                            textAlign: 'left',
                                            color: '#1e293b',
                                            fontSize: '0.82rem',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.4rem',
                                            transition: 'background-color 0.15s ease'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <span>📊</span> Summary
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Filter Bar for Everyone */}
            <div style={{ marginBottom: '1rem' }}>
                <DGFilterBar onFilterChange={setFilters} />
            </div>

            {error && <div style={{ padding: '1rem', background: '#fee', color: '#c00', borderRadius: '4px', marginBottom: '1rem' }}>{error}</div>}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingRight: '4px', paddingBottom: '2rem' }}>
                {isCreating && (
                    <div style={{ marginBottom: '2rem' }}>
                        <EventForm onSubmit={handleCreate} onCancel={() => setIsCreating(false)} initialData={initialFormState} user={user} users={users} />
                    </div>
                )}

                {loading ? (
                <ContentLoader text={`Loading ${displayDivisionName} events...`} />
            ) : (
                <div style={{ display: 'grid', gap: '1rem' }}>
                    {events.length === 0 ? (
                        <p style={{ color: '#666', fontStyle: 'italic' }}>No events found matching your filters.</p>
                    ) : (
                        events.map(event => {
                            const isExpanded = expandedEventId === event.EventID;
                            const eventSubevents = subevents[event.EventID] || [];

                            // Calculate Progress
                            let totalSub = 0;
                            let completedSub = 0;

                            if (typeof event.TotalSubEvents === 'number') {
                                totalSub = event.TotalSubEvents;
                                completedSub = event.CompletedSubEvents || 0;
                            } else {
                                const subs = subevents[event.EventID] || [];
                                totalSub = subs.length;
                                completedSub = subs.filter(s => s.Status === 'Completed').length;
                            }
                            let progressPercent = 0;

                            if (totalSub > 0) {
                                progressPercent = Math.round((completedSub / totalSub) * 100);
                            } else if (event.Status === 'Completed') {
                                progressPercent = 100;
                            }

                            // Card colors are based on status/progress only (not expand state).
                            let cardBackground = 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)';
                            let borderColor = '#e9ecef';

                            if (event.Status === 'Completed') {
                                cardBackground = '#d1e7dd';
                                borderColor = '#badbcc';
                            } else if (event.Status === 'Cancelled') {
                                cardBackground = '#f8d7da';
                                borderColor = '#f5c6cb';
                            } else if (totalSub > 0 && completedSub === totalSub) {
                                cardBackground = '#fff3cd';
                                borderColor = '#ffecb5';
                            } else if (progressPercent > 0 || event.Status === 'In Progress') {
                                cardBackground = 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)';
                                borderColor = '#93c5fd';
                            }

                            return (
                                <div key={event.EventID} className="modern-card" style={{
                                    padding: 0, overflow: 'visible',
                                    border: `1px solid ${borderColor}`,
                                    borderLeft: isExpanded ? '4px solid #007bff' : `1px solid ${borderColor}`,
                                    boxShadow: isExpanded ? '0 8px 16px rgba(0,123,255,0.15)' : '0 2px 4px rgba(0,0,0,0.05)',
                                    marginBottom: '1rem', borderRadius: '12px',
                                    background: cardBackground,
                                    transition: 'all 0.3s ease'
                                }}>
                                    <div
                                        onClick={() => toggleExpand(event.EventID)}
                                        style={{
                                            padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem',
                                            cursor: 'pointer',
                                            background: 'transparent',
                                            borderBottom: isExpanded ? '1px solid #90caf9' : 'none',
                                            transition: 'background 0.2s'
                                        }}
                                    >
                                        {/* 1. Icon (Fixed) */}
                                        <div style={{ flexShrink: 0 }}>
                                            {isExpanded ? <ChevronDown size={20} color="#666" /> : <ChevronRight size={20} color="#666" />}
                                        </div>

                                        {/* 2. Title & Details (Flexible) */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <h4 style={{ margin: '0 0 0.25rem 0', whiteWhiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {event.Title}
                                                {event.LinkedTaskIDs && event.LinkedTaskIDs.length > 0 && (
                                                    <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', background: '#e0f2fe', color: '#0369a1', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>
                                                        {event.LinkedTaskIDs.length} Task(s)
                                                    </span>
                                                )}
                                            </h4>
                                            <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                                                {event.Category || event.EventType} {event.SubType ? `• ${event.SubType}` : ''} • {
                                                    event.Status === 'Completed' ? <span style={{ color: '#28a745' }}>Completed on {formatDate(event.EndDateTime || event.StartDateTime)}</span> :
                                                        event.Status === 'Cancelled' ? <span style={{ color: '#dc3545' }}>Cancelled on {formatDate(event.EndDateTime || event.StartDateTime)}</span> :
                                                            formatEventDate(event.StartDateTime)
                                                }
                                            </p>
                                            <p style={{ margin: '0.2rem 0 0 0', color: '#888', fontSize: '0.8rem' }}>
                                                Created by: {getUserName(event.CreatedBy, users)}
                                            </p>
                                            
                                            {/* Beautiful flat Panelists Row */}
                                            {((event.Panelists || event.panelists || []).length > 0) && (
                                                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.78rem', color: '#475569', lineHeight: 1.4 }}>
                                                    <span style={{ fontWeight: 600, color: '#334155' }}>Invited Experts:</span>{' '}
                                                    {(event.Panelists || event.panelists || []).map(p => p.name).join(', ')}
                                                </p>
                                            )}

                                            {/* Flat Key Event Metadata Row */}
                                            {(event.ChiefGuest || event.chiefGuest || event.Location || event.location || event.Mode || event.mode) && (
                                                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#64748b', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                                    {(event.Mode || event.mode) && (
                                                        <span>
                                                            <span style={{ fontWeight: 600, color: '#475569' }}>Mode:</span> {event.Mode || event.mode}
                                                        </span>
                                                    )}
                                                    {(event.Location || event.location) && (
                                                        <span>
                                                            <span style={{ color: '#cbd5e1', marginRight: '0.5rem' }}>•</span>
                                                            <span style={{ fontWeight: 600, color: '#475569' }}>Venue:</span> {event.Location || event.location}
                                                        </span>
                                                    )}
                                                    {(event.ChiefGuest || event.chiefGuest) && (
                                                        <span>
                                                            <span style={{ color: '#cbd5e1', marginRight: '0.5rem' }}>•</span>
                                                            <span style={{ fontWeight: 600, color: '#c2410c' }}>Guest:</span> {event.ChiefGuest || event.chiefGuest}
                                                        </span>
                                                    )}
                                                </p>
                                            )}

                                            <p style={{ margin: '0.35rem 0 0 0', color: '#666', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                <Paperclip size={13} />
                                                {(Array.isArray(event.Attachments) ? event.Attachments.length : 0)} file(s) attached
                                            </p>
                                        </div>

                                        {/* 3. Circular Progress Ring (Fixed) */}
                                        <div style={{ flexShrink: 0, position: 'relative', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <svg width="40" height="40" viewBox="0 0 40 40">
                                                <circle cx="20" cy="20" r="16" fill="none" stroke="#e9ecef" strokeWidth="4" />
                                                <circle
                                                    cx="20" cy="20" r="16" fill="none" stroke={progressPercent === 100 ? '#28a745' : '#007bff'} strokeWidth="4"
                                                    strokeDasharray="100.53"
                                                    strokeDashoffset={100.53 - (progressPercent / 100) * 100.53}
                                                    transform="rotate(-90 20 20)"
                                                    strokeLinecap="round"
                                                    style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                                                />
                                            </svg>
                                            <span style={{ position: 'absolute', fontSize: '0.65rem', fontWeight: 'bold', color: progressPercent === 100 ? '#28a745' : '#007bff' }}>
                                                {progressPercent}%
                                            </span>
                                        </div>

                                        <div style={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                                            <input
                                                type="file"
                                                multiple
                                                ref={(el) => { fileInputRefs.current[event.EventID] = el; }}
                                                onChange={(e) => handleAttachmentSelection(event.EventID, e)}
                                                style={{ display: 'none' }}
                                            />
                                            <select
                                                value={uploadCategoryByEvent[event.EventID] || 'CONCEPT_AGENDA'}
                                                onChange={(e) => setUploadCategoryByEvent(prev => ({ ...prev, [event.EventID]: e.target.value }))}
                                                style={{
                                                    border: '1px solid #cbd5e1',
                                                    background: '#fff',
                                                    color: '#1f2937',
                                                    borderRadius: '8px',
                                                    padding: '0.3rem 0.4rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    marginBottom: '0.3rem',
                                                    width: '148px'
                                                }}
                                                title="Select upload category"
                                            >
                                                {CATEGORY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                            <button
                                                onClick={() => handleAttachmentPicker(event.EventID)}
                                                style={{
                                                    border: '1px solid #ced4da',
                                                    background: '#fff',
                                                    color: '#334155',
                                                    borderRadius: '8px',
                                                    padding: '0.35rem 0.55rem',
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    gap: '0.35rem',
                                                    alignItems: 'center',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600
                                                }}
                                                title="Upload files"
                                            >
                                                <Upload size={14} />
                                                Upload
                                            </button>
                                            {uploadingByEvent[event.EventID]?.active && (
                                                <div style={{ marginTop: '0.25rem', fontSize: '0.7rem', color: '#0f766e', textAlign: 'center' }}>
                                                    Uploading {uploadingByEvent[event.EventID].done}/{uploadingByEvent[event.EventID].total}
                                                </div>
                                            )}
                                        </div>

                                        {/* 4. Status Dropdown (Fixed) */}
                                        <div style={{ flexShrink: 0, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                                            <select
                                                value={event.Status}
                                                disabled={isEmployee} // Freeze for Employees
                                                onChange={async (e) => {
                                                    try {
                                                        const newStatus = e.target.value;
                                                        // Only send the status field (backend expects `status` lowercase)
                                                        await eventService.update(event.EventID, { status: newStatus });

                                                        // Optimistic Update
                                                        setEvents(prev => prev.map(ev => ev.EventID === event.EventID ? { ...ev, Status: newStatus } : ev));
                                                        setAllEvents(prev => prev.map(ev => ev.EventID === event.EventID ? { ...ev, Status: newStatus } : ev));

                                                        // Parent Completed => all sub-events Completed + parent progress 100%.
                                                        if (newStatus === 'Completed') {
                                                            let subs = subevents[event.EventID];
                                                            if (!Array.isArray(subs)) {
                                                                try {
                                                                    subs = await eventService.getSubEvents(event.EventID);
                                                                } catch (fetchErr) {
                                                                    console.warn(`Failed to fetch subevents for ${event.EventID}`, fetchErr);
                                                                    subs = [];
                                                                }
                                                            }

                                                            const completedSubs = (subs || []).map(sub => ({ ...sub, Status: 'Completed' }));
                                                            const total = completedSubs.length;
                                                            const completed = total;

                                                            setSubevents(prev => ({ ...prev, [event.EventID]: completedSubs }));
                                                            setEvents(prev => prev.map(ev => ev.EventID === event.EventID ? { ...ev, TotalSubEvents: total, CompletedSubEvents: completed, Status: 'Completed' } : ev));
                                                            setAllEvents(prev => prev.map(ev => ev.EventID === event.EventID ? { ...ev, TotalSubEvents: total, CompletedSubEvents: completed, Status: 'Completed' } : ev));
                                                        }
                                                    } catch (err) {
                                                        console.error("Update failed", err);
                                                        alert("Failed to update event status: " + err.message);
                                                    }
                                                }}
                                                style={{
                                                    fontSize: '0.8rem', fontWeight: 500,
                                                    padding: '0.2rem 0.5rem', borderRadius: '4px',
                                                    border: '1px solid #ccc',
                                                    background: event.Status === 'Cancelled' ? '#f8d7da' : (event.Status === 'Completed' ? '#d4edda' : '#e2e6ea'),
                                                    color: event.Status === 'Cancelled' ? '#721c24' : (event.Status === 'Completed' ? '#155724' : '#333'),
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <option value="Scheduled">Scheduled</option>
                                                <option value="In Progress">In Progress</option>
                                                <option value="Completed">Completed</option>
                                                <option value="Cancelled">Cancelled</option>
                                            </select>
                                        </div>
                                    </div>



                                    {isExpanded && (
                                        <div style={{ padding: '1rem', borderTop: '1px solid #eee', background: '#fafafa' }}>
                                            <div style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                                                <h5 style={{ margin: '0 0 0.5rem 0', color: '#334155' }}>Attachments</h5>
                                                {(Array.isArray(event.Attachments) && event.Attachments.length > 0) ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                        {CATEGORY_OPTIONS.map((cat) => {
                                                            const inCategory = event.Attachments.filter(a => (a.Category || 'CONCEPT_AGENDA') === cat.value);
                                                            if (inCategory.length === 0) return null;
                                                            return (
                                                                <div key={cat.value}>
                                                                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                                                        {cat.label}
                                                                    </div>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', maxHeight: '156px', overflowY: 'auto', paddingRight: '0.2rem' }}>
                                                                        {inCategory.map((attachment) => (
                                                                            <div key={attachment.AttachmentID} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.45rem 0.6rem' }}>
                                                                                <div style={{ minWidth: 0 }}>
                                                                                    <div style={{ fontSize: '0.85rem', color: '#1f2937', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                        {attachment.FileName}
                                                                                    </div>
                                                                                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                                                        {(attachment.ContentType || 'file')} {attachment.SizeBytes ? `• ${(attachment.SizeBytes / 1024 / 1024).toFixed(2)} MB` : ''}
                                                                                    </div>
                                                                                </div>
                                                                                <div style={{ display: 'flex', gap: '0.35rem' }}>
                                                                                    <button
                                                                                        onClick={(e) => { e.stopPropagation(); openAttachment(event.EventID, attachment.AttachmentID, false); }}
                                                                                        style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: '6px', padding: '0.25rem 0.45rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem' }}
                                                                                    >
                                                                                        <Eye size={13} /> Preview
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={(e) => { e.stopPropagation(); openAttachment(event.EventID, attachment.AttachmentID, true); }}
                                                                                        style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: '6px', padding: '0.25rem 0.45rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem' }}
                                                                                    >
                                                                                        <Download size={13} /> Download
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={(e) => { e.stopPropagation(); deleteAttachment(event.EventID, attachment.AttachmentID); }}
                                                                                        style={{ border: '1px solid #fecaca', background: '#fff1f2', color: '#b91c1c', borderRadius: '6px', padding: '0.25rem 0.45rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem' }}
                                                                                        title="Delete attachment"
                                                                                    >
                                                                                        <Trash2 size={13} />
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>No files attached yet.</p>
                                                )}
                                            </div>

                                            {/* Event Panelists Block */}
                                            {event.Panelists && event.Panelists.length > 0 && (
                                                <div style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                                                    <h5 style={{ margin: '0 0 0.5rem 0', color: '#334155', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Event Panelists</h5>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                        {event.Panelists.map((panelist, pIndex) => (
                                                            <span
                                                                key={pIndex}
                                                                style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                                                                    background: panelist.type === 'internal' ? '#eff6ff' : '#ecfdf5',
                                                                    color: panelist.type === 'internal' ? '#1e40af' : '#065f46',
                                                                    border: `1px solid ${panelist.type === 'internal' ? '#bfdbfe' : '#a7f3d0'}`,
                                                                    fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: '6px',
                                                                    fontWeight: 600
                                                                }}
                                                                title={panelist.type === 'internal' ? 'RIS Team Member' : 'External Expert'}
                                                            >
                                                                <span style={{
                                                                    width: '6px', height: '6px', borderRadius: '50%',
                                                                    background: panelist.type === 'internal' ? '#3b82f6' : '#10b981'
                                                                }}></span>
                                                                {panelist.name || 'Unknown Panelist'}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Linked Tasks Editable Block */}
                                            <div style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                                    <h5 style={{ margin: 0, color: '#334155' }}>Linked Tasks</h5>
                                                    {editingLinkFor !== event.EventID ? (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditingLinkFor(event.EventID);
                                                                setTempLinkedTasks(event.LinkedTaskIDs || []);
                                                                setLinkTaskSearchQuery('');
                                                            }}
                                                            style={{ fontSize: '0.75rem', background: 'none', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer', color: '#007bff' }}
                                                        >
                                                            Edit Links
                                                        </button>
                                                    ) : (
                                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditingLinkFor(null);
                                                                    setLinkTaskSearchQuery('');
                                                                }}
                                                                style={{ fontSize: '0.75rem', background: '#f8f9fa', border: '1px solid #ced4da', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer', color: '#495057' }}
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleSaveLinkedTasks(event.EventID); }}
                                                                disabled={loadingLinks}
                                                                style={{ fontSize: '0.75rem', background: '#007bff', border: 'none', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer', color: 'white', opacity: loadingLinks ? 0.7 : 1 }}
                                                            >
                                                                {loadingLinks ? 'Saving...' : 'Save'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                {editingLinkFor === event.EventID ? (
                                                    <div onClick={e => e.stopPropagation()}>
                                                        <input
                                                            type="text"
                                                            placeholder="Search tasks..."
                                                            value={linkTaskSearchQuery}
                                                            onChange={e => setLinkTaskSearchQuery(e.target.value)}
                                                            style={{ width: '100%', padding: '0.4rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', marginBottom: '0.5rem' }}
                                                        />
                                                        <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '0.5rem', background: '#f8fafc' }}>
                                                            {tasksList.length === 0 ? (
                                                                <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>No available tasks.</div>
                                                            ) : (
                                                                tasksList.filter(t => t.Title?.toLowerCase().includes(linkTaskSearchQuery.toLowerCase())).map(task => {
                                                                    const isSelected = tempLinkedTasks.includes(task.TaskID);
                                                                    return (
                                                                        <label key={task.TaskID} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0.5rem', cursor: 'pointer', borderRadius: '4px' }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isSelected}
                                                                                onChange={() => {
                                                                                    setTempLinkedTasks(prev =>
                                                                                        isSelected ? prev.filter(id => id !== task.TaskID) : [...prev, task.TaskID]
                                                                                    );
                                                                                }}
                                                                                style={{ cursor: 'pointer' }}
                                                                            />
                                                                            <span style={{ fontSize: '0.85rem', color: '#334155' }}>{task.Title}</span>
                                                                        </label>
                                                                    );
                                                                })
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        {(!event.LinkedTaskIDs || event.LinkedTaskIDs.length === 0) ? (
                                                            <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>No tasks linked yet.</p>
                                                        ) : (
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                                {event.LinkedTaskIDs.map(taskId => {
                                                                    const t = tasksList.find(ts => ts.TaskID === taskId);
                                                                    return (
                                                                        <span
                                                                            key={taskId}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                navigate(`/tasks?taskId=${taskId}`);
                                                                            }}
                                                                            style={{ background: '#e0f2fe', color: '#0369a1', fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: '4px', fontWeight: 500, cursor: 'pointer', transition: 'background 0.2s' }}
                                                                            onMouseEnter={(e) => e.target.style.background = '#bae6fd'}
                                                                            onMouseLeave={(e) => e.target.style.background = '#e0f2fe'}
                                                                            title="Click to view task"
                                                                        >
                                                                            {t ? t.Title : `Task ID: ${taskId}`}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <h5 style={{ margin: '0 0 1rem 0', color: '#555' }}>Sub-events</h5>

                                            {eventSubevents.length === 0 && <p style={{ fontSize: '0.9rem', color: '#999', fontStyle: 'italic' }}>No sub-events.</p>}

                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                                                {eventSubevents.map((sub, index) => {
                                                    let statusIcon = <Circle size={16} color="#adb5bd" fill="white" />;
                                                    if (sub.Status === 'Completed') {
                                                        statusIcon = <ShieldCheck size={18} color="#28a745" fill="#e6fffa" />;
                                                    } else if (sub.Status === 'Cancelled') {
                                                        statusIcon = <X size={18} color="#dc3545" />;
                                                    } else if (sub.Status === 'In Progress') {
                                                        statusIcon = <Circle size={18} color="#007bff" fill="#e7f1ff" />;
                                                    }

                                                    // Status Options based on Role
                                                    const allowedStatuses = isEmployee
                                                        ? ['Review', 'Completed']
                                                        : ['Pending', 'In Progress', 'Completed', 'Cancelled'];

                                                    return (
                                                        <div key={sub.SubEventID} style={{ display: 'flex', flexDirection: 'column', background: 'white', borderRadius: '8px', border: '1px solid #eee', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', marginBottom: '0.75rem' }}>
                                                            <div style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: '30px 1fr 120px 120px', // Icon, Title, Date, Status
                                                                alignItems: 'center',
                                                                padding: '0.75rem',
                                                            }}>
                                                                {/* Icon */}
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    {statusIcon}
                                                                </div>

                                                                {/* Title & Comment Icon */}
                                                                <div style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '0.5rem',
                                                                    fontWeight: 600,
                                                                    color: '#495057',
                                                                    textDecoration: sub.Status === 'Cancelled' ? 'line-through' : 'none',
                                                                    textDecorationColor: 'red',
                                                                    position: 'relative'
                                                                }}>
                                                                    <div
                                                                        style={{ position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                                                        onClick={(e) => { e.stopPropagation(); setActiveCommentModal(activeCommentModal === sub.SubEventID ? null : sub.SubEventID); }}
                                                                    >
                                                                        <MessageCircle size={16} color={sub.Comments && sub.Comments.length > 0 ? '#007bff' : '#adb5bd'} />
                                                                        {sub.Comments && sub.Comments.length > 0 && (
                                                                            <span style={{ position: 'absolute', top: '-5px', right: '-8px', background: '#dc3545', color: 'white', borderRadius: '50%', padding: '0.1rem 0.3rem', fontSize: '0.6rem', fontWeight: 'bold' }}>
                                                                                {sub.Comments.length}
                                                                            </span>
                                                                        )}
                                                                        {activeCommentModal === sub.SubEventID && (
                                                                            <div style={{
                                                                                position: 'absolute', top: '100%', left: '0', marginTop: '0.5rem',
                                                                                width: '280px', background: 'white', border: '1px solid #ccc',
                                                                                borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                                                zIndex: 1000, padding: '0.75rem', cursor: 'default',
                                                                                fontWeight: 'normal'
                                                                            }}
                                                                                onClick={e => e.stopPropagation()}
                                                                            >
                                                                                <h6 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#333' }}>Comments</h6>
                                                                                {sub.Comments && sub.Comments.length > 0 ? (
                                                                                    <div style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                                                        {sub.Comments.map((c, i) => (
                                                                                            <div key={i} style={{ fontSize: '0.8rem', background: '#f8f9fa', padding: '0.4rem', borderRadius: '4px', border: '1px solid #e9ecef' }}>
                                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                                                                                                    <strong style={{ color: '#495057' }}>{c.AddedByName || 'Unknown'}</strong>
                                                                                                </div>
                                                                                                <div style={{ color: '#333', whiteSpace: 'pre-wrap' }}>{c.Text}</div>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : (
                                                                                    <div style={{ fontSize: '0.8rem', color: '#999', fontStyle: 'italic', marginBottom: '0.5rem' }}>No previous comments.</div>
                                                                                )}
                                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                                                    <textarea
                                                                                        value={subeventComments[sub.SubEventID] || ''}
                                                                                        onChange={e => setSubeventComments({ ...subeventComments, [sub.SubEventID]: e.target.value })}
                                                                                        placeholder="Add a comment (max 20 words)..."
                                                                                        rows={2}
                                                                                        style={{ padding: '0.4rem', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '0.8rem', width: '100%', resize: 'vertical' }}
                                                                                    />
                                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                        <span style={{ fontSize: '0.7rem', color: ((subeventComments[sub.SubEventID] || '').trim() ? (subeventComments[sub.SubEventID] || '').trim().split(/\s+/).length : 0) > 20 ? '#dc3545' : '#6c757d' }}>
                                                                                            {((subeventComments[sub.SubEventID] || '').trim() ? (subeventComments[sub.SubEventID] || '').trim().split(/\s+/).length : 0)}/20
                                                                                        </span>
                                                                                        <button
                                                                                            disabled={!subeventComments[sub.SubEventID] || subeventComments[sub.SubEventID].trim().length === 0 || (subeventComments[sub.SubEventID].trim().split(/\s+/).length > 20)}
                                                                                            onClick={(e) => { e.stopPropagation(); handleAddSubeventComment(event.EventID, sub.SubEventID, sub.Status); }}
                                                                                            style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: (!subeventComments[sub.SubEventID] || subeventComments[sub.SubEventID].trim().length === 0 || (subeventComments[sub.SubEventID].trim().split(/\s+/).length > 20)) ? 'not-allowed' : 'pointer', opacity: (!subeventComments[sub.SubEventID] || subeventComments[sub.SubEventID].trim().length === 0 || (subeventComments[sub.SubEventID].trim().split(/\s+/).length > 20)) ? 0.6 : 1 }}
                                                                                        >
                                                                                            Send
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {sub.Title}
                                                                </div>

                                                                {/* Date */}
                                                                <div style={{ fontSize: '0.85rem', color: '#868e96', textAlign: 'right', paddingRight: '1rem' }}>
                                                                    {sub.StartDateTime ? new Date(sub.StartDateTime).toLocaleDateString() : 'No Date'}
                                                                </div>

                                                                {/* Status Action */}
                                                                <div style={{ position: 'relative' }}>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSubeventStatusMenuOpen(subeventStatusMenuOpen === sub.SubEventID ? null : sub.SubEventID);
                                                                        }}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '0.3rem 0.5rem',
                                                                            background: 'white',
                                                                            border: '1px solid #ddd',
                                                                            borderRadius: '4px',
                                                                            fontSize: '0.8rem',
                                                                            color: '#333',
                                                                            cursor: 'pointer',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between'
                                                                        }}
                                                                    >
                                                                        {sub.Status || 'Scheduled'} <ChevronDown size={14} />
                                                                    </button>

                                                                    {subeventStatusMenuOpen === sub.SubEventID && (
                                                                        <div style={{
                                                                            position: 'absolute', top: '100%', right: 0,
                                                                            marginTop: '0.25rem', background: 'white', border: '1px solid #ccc',
                                                                            borderRadius: '4px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                                                            zIndex: 100, minWidth: '120px', padding: '0.5rem'
                                                                        }}>
                                                                            {allowedStatuses.map(status => (
                                                                                <div
                                                                                    key={status}
                                                                                    onClick={async (e) => {
                                                                                        e.stopPropagation();
                                                                                        try {
                                                                                            await eventService.updateSubEvent(sub.SubEventID, status);
                                                                                            // Refresh subevent list
                                                                                            const subs = await eventService.getSubEvents(event.EventID);
                                                                                            setSubevents(prev => ({ ...prev, [event.EventID]: subs }));
                                                                                            const total = subs.length;
                                                                                            const completed = subs.filter(s => s.Status === 'Completed').length;
                                                                                            setEvents(prev => prev.map(ev => ev.EventID === event.EventID ? { ...ev, TotalSubEvents: total, CompletedSubEvents: completed } : ev));
                                                                                            setAllEvents(prev => prev.map(ev => ev.EventID === event.EventID ? { ...ev, TotalSubEvents: total, CompletedSubEvents: completed } : ev));
                                                                                            setSubeventStatusMenuOpen(null);
                                                                                        } catch (err) {
                                                                                            alert("Failed to update subevent status:\n" + err.message);
                                                                                        }
                                                                                    }}
                                                                                    style={{
                                                                                        fontSize: '0.8rem', padding: '0.4rem', cursor: 'pointer',
                                                                                        color: status === 'Completed' ? '#28a745' : status === 'Cancelled' ? '#dc3545' : '#17a2b8',
                                                                                        fontWeight: status === sub.Status ? 600 : 400,
                                                                                        transition: 'background 0.1s'
                                                                                    }}
                                                                                    onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                                                                                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                                                                                >
                                                                                    {status}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {
                                                creatingSubeventFor === event.EventID ? (
                                                    <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #ddd' }}>
                                                        <h6 style={{ marginTop: 0 }}>New Sub-event</h6>
                                                        <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                                            <input
                                                                placeholder="Title"
                                                                value={newSubeventData.title}
                                                                onChange={e => setNewSubeventData({ ...newSubeventData, title: e.target.value })}
                                                                style={{ padding: '0.4rem', borderRadius: '4px', border: '1px solid #ccc' }}
                                                            />
                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                                                                <input type="datetime-local" value={newSubeventData.startDateTime} onChange={e => setNewSubeventData({ ...newSubeventData, startDateTime: e.target.value })} style={{ padding: '0.4rem', border: '1px solid #ccc' }} />
                                                                <input type="datetime-local" value={newSubeventData.endDateTime} onChange={e => setNewSubeventData({ ...newSubeventData, endDateTime: e.target.value })} style={{ padding: '0.4rem', border: '1px solid #ccc' }} />
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                            <button onClick={() => handleCreateSubevent(event.EventID)} style={{ padding: '0.3rem 0.8rem', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save</button>
                                                            <button onClick={() => setCreatingSubeventFor(null)} style={{ padding: '0.3rem 0.8rem', background: '#ccc', color: 'black', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => setCreatingSubeventFor(event.EventID)}
                                                        style={{ fontSize: '0.85rem', color: '#007bff', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                                                    >
                                                        <Plus size={14} /> Add Sub-event
                                                    </button>
                                                )
                                            }
                                        </div >
                                    )
                                    }
                                </div >
                            );
                        })
                    )}
                </div>
            )}
            </div>
        </div>
    );
};

export default Events;
